/**
 * Overlay component for Google Calendar events in the time grid.
 * Positions and styles GCal events as semi-transparent blocks.
 */
import { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  TIME_SLOT_MINS,
  MIN_GCAL_EVENT_HEIGHT_PX,
  CALENDAR_START_HOUR,
} from '@/config/constants';
import {
  planDayItemFontSize,
  planDayItemShortFontSize,
  PLAN_LINE_HEIGHT_SHORT,
  PLAN_LINE_HEIGHT_DEFAULT,
} from '@/components/ui/taskVisualTokens';

const GCalEventOverlay = ({
  day,
  fifteenMinuteSlotHeight,
  events,
  isEnabled,
  startHour = CALENDAR_START_HOUR,
}) => {
  const overlayEvents = useMemo(() => {
    if (!isEnabled || !events || events.length === 0) {
      return [];
    }

    return events
      .map((event) => {
        const startTime = new Date(event.start.dateTime || event.start.date);
        const endTime = new Date(event.end.dateTime || event.end.date);

        // Compare dates only (ignore time)
        const eventDate = new Date(
          startTime.getFullYear(),
          startTime.getMonth(),
          startTime.getDate(),
        );
        const dayDate = new Date(
          day.date.getFullYear(),
          day.date.getMonth(),
          day.date.getDate(),
        );

        // Skip if event is not on this day
        if (eventDate.getTime() !== dayDate.getTime()) {
          return null;
        }

        const minutesSinceCalendarStart =
          startTime.getHours() * 60 + startTime.getMinutes() - startHour * 60;
        const durationMinutes = Math.ceil(
          (endTime.getTime() - startTime.getTime()) / (1000 * 60),
        );

        const slotIndex = minutesSinceCalendarStart / TIME_SLOT_MINS;
        const topOffset = slotIndex * fifteenMinuteSlotHeight;
        const heightPixels =
          (durationMinutes / TIME_SLOT_MINS) * fifteenMinuteSlotHeight;
        const height = Math.max(
          heightPixels,
          Math.min(MIN_GCAL_EVENT_HEIGHT_PX, fifteenMinuteSlotHeight),
        );

        return {
          id: event.id,
          summary: event.summary || '(No title)',
          top: topOffset,
          height,
          isShort: height <= fifteenMinuteSlotHeight,
          htmlLink: event.htmlLink || null,
          startTime,
          endTime,
        };
      })
      .filter(Boolean);
  }, [events, day, isEnabled, fifteenMinuteSlotHeight, startHour]);

  if (
    !isEnabled ||
    !events ||
    events.length === 0 ||
    overlayEvents.length === 0
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
      data-testid="overlay-container"
    >
      {overlayEvents.map((event) => (
        <Box
          key={event.id}
          data-testid="event-block"
          style={{ top: `${event.top}px`, height: `${event.height}px` }}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            width: '100%',
            p: event.isShort ? '0 3px' : '2px 4px',
            bgcolor: 'transparent',
            border: '1px dashed',
            borderColor: 'text.disabled',
            borderRadius: '2px',
            overflow: 'hidden',
            pointerEvents: 'none',
            fontSize: event.isShort
              ? planDayItemShortFontSize
              : planDayItemFontSize,
            lineHeight: event.isShort
              ? PLAN_LINE_HEIGHT_SHORT
              : PLAN_LINE_HEIGHT_DEFAULT,
            fontWeight: 400,
            fontStyle: 'italic',
          }}
          title={`${event.summary} (${event.startTime.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })} - ${event.endTime.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })})`}
        >
          <Box
            component="span"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: event.isShort ? 1 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              color: 'text.subtle',
              overflowWrap: 'anywhere',
              fontStyle: 'italic',
            }}
          >
            {event.summary}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default GCalEventOverlay;
